import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QRCodeSVG } from "qrcode.react";

import autorizacoesService from "../services/autorizacoes";
import {
  obterNomeExibicaoUsuario,
  obterRotuloNomeSolicitante,
} from "../utils/obterNomeExibicaoUsuario";
import pdfLayoutService from "../services/pdfLayout";
import usuarioService from "../services/usuario";
import authService from "../services/auth";
import licencaHeaderImg from "../assets/images/licenca-header.png";
import licencaFooterImg from "../assets/images/licenca-footer.png";
import CharCounter from "../components/CharCounter";
import NumeroProcessoField, {
  ehNumeroInformadoValido,
  montarNumeroProcessoPreview,
} from "../components/NumeroProcessoField";
import {
  formatCpfCnpj,
  formatCEP,
  formatDateToShow,
} from "../utils/formatters";
import {
  parseAssinaturaSecretarioResponse,
  resolverFormatoImagemAssinatura,
} from "../utils/assinaturaSecretario";
import {
  obterCodigoTipoAutorizacao,
  tipoAutorizacaoEnum,
} from "../utils/constants";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import {
  html2canvasForPdf,
  PDF_DOCUMENT_WIDTH_PX,
  PDF_PAGE_HEIGHT_MM,
  PDF_PAGE_WIDTH_MM,
  addSlicedContentImage,
  canvasHeightInMm,
  canvasToJpegDataUrl,
  getContentPaging,
  withFixedPdfCaptureWidth,
} from "../utils/pagedHtmlPdf";

const CONDICIONANTES_MAX = 3000;
const OBSERVACOES_MAX = 1000;

const IMPORTANTE_TEXTO =
  "ESTA LICENÇA NÃO DISPENSA E NEM SUBSTITUI QUAISQUER OUTROS TIPOS DE CERTIDÕES, ALVARÁS, LICENÇAS OU AUTORIZAÇÕES DE QUALQUER NATUREZA, EXIGIDOS PARA O EXERCÍCIO DA ATIVIDADE";

const NIVEIS_PRESSAO_SONORA = [
  { categoria: "Áreas de sítios e fazendas (rurais)", diurno: "40", noturno: "35" },
  { categoria: "Área estritamente residencial urbana", diurno: "50", noturno: "45" },
  { categoria: "Área mista, predominantemente residencial", diurno: "55", noturno: "50" },
  { categoria: "Área mista, com vocação comercial e administrativa", diurno: "60", noturno: "55" },
  { categoria: "Área mista, com vocação recreacional e cultural", diurno: "65", noturno: "55" },
  { categoria: "Área predominantemente industrial", diurno: "70", noturno: "60" },
];

const tipoAutorizacaoLabel = (valor) => {
  const item = tipoAutorizacaoEnum.find((t) => t.value === valor);
  return item ? item.label : "Autorização";
};

function EmitirAutorizacao() {
  const { id } = useParams();
  const navigate = useNavigate();

  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const footerRef = useRef(null);
  const tabelaRef = useRef(null);
  const qrRef = useRef(null);
  const previewRef = useRef(null);

  const [solicitacao, setSolicitacao] = useState(null);
  const [solicitante, setSolicitante] = useState(null);
  const [usuarioAtual, setUsuarioAtual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emitindo, setEmitindo] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [error, setError] = useState("");

  const [condicionantes, setCondicionantes] = useState(
    "1- É proibido perturbar o sossego e o bem-estar públicos com ruídos ou vibrações de qualquer natureza que ultrapassem os níveis legalmente previstos para os diferentes horários e zonas de uso.\n\n" +
    "2- O descumprimento das condicionantes da presente Autorização implicará no seu cancelamento, de acordo com a Resolução CONAMA nº 237/97, bem como na aplicação das penalidades previstas na legislação ambiental, sem prejuízo da obrigação de reparar quaisquer danos ambientais causados;\n\n" +
    "3- A inobservância do limite tolerado poderá incorrer em multas e demais sanções previstas nas leis Nº 002/2014 (Código de Posturas do Município de Novo Oriente); Lei 848/2021 (Política Municipal do Meio Ambiente); Lei 9605/98 (Lei de Crimes Ambientais) e Lei 13.711/2005 e Decreto 34.704/2022 (Medidas de combate à poluição sonora gerada por estabelecimentos comerciais e por veículos no Estado do Ceará e dá outras providências)."
  );
  const [observacoes, setObservacoes] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFim, setHoraFim] = useState("");
  const [numeroInformado, setNumeroInformado] = useState("");
  const anoProcesso = new Date().getFullYear();

  const [headerImgSrc, setHeaderImgSrc] = useState(null);
  const [footerImgSrc, setFooterImgSrc] = useState(null);
  const [assinaturaDataUrl, setAssinaturaDataUrl] = useState(null);
  const [assinaturaMensagem, setAssinaturaMensagem] = useState(null);

  const carregarPdfLayout = useCallback(async () => {
    try {
      const res = await pdfLayoutService.obterLatest();
      const layoutId = res.data?.data;

      const toDataUrl = (blob) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

      const [headerRes, footerRes, assinaturaRes] = await Promise.all([
        layoutId ? pdfLayoutService.getHeaderImage(layoutId).catch(() => null) : Promise.resolve(null),
        layoutId ? pdfLayoutService.getFooterImage(layoutId).catch(() => null) : Promise.resolve(null),
        autorizacoesService.getAssinaturaPadrao().catch(() => null),
      ]);

      setHeaderImgSrc(headerRes ? await toDataUrl(headerRes.data) : licencaHeaderImg);
      setFooterImgSrc(footerRes ? await toDataUrl(footerRes.data) : licencaFooterImg);

      const assinatura = await parseAssinaturaSecretarioResponse(assinaturaRes);
      if (assinatura.cadastrada) {
        setAssinaturaDataUrl(assinatura.dataUrl);
        setAssinaturaMensagem(null);
      } else {
        setAssinaturaDataUrl(null);
        setAssinaturaMensagem(assinatura.message);
      }
    } catch {
      setHeaderImgSrc(licencaHeaderImg);
      setFooterImgSrc(licencaFooterImg);
    }
  }, []);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const data = await autorizacoesService.getById(id);
      setSolicitacao(data);

      if (data?.createUserId) {
        try {
          const usuarioData = await usuarioService.obterUsuarioPorId(
            data.createUserId
          );
          setSolicitante(usuarioData);
        } catch {
          /* opcional */
        }
      }
    } catch {
      setError("Erro ao carregar a solicitação de autorização.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarDados();
    carregarPdfLayout();
    setUsuarioAtual(authService.getCurrentUser());
  }, [carregarDados, carregarPdfLayout]);

  const gerarPdfBlob = async () => {
    return withFixedPdfCaptureWidth(previewRef.current, async () => {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = PDF_PAGE_WIDTH_MM;
      const pageHeight = PDF_PAGE_HEIGHT_MM;

      const SIG_DATE_X_MM = 10;
      const SIG_DATE_W_MM = 60;
      const SIG_BOX_X_MM = 72;
      const SIG_BOX_Y_TOP_MM = 242;
      const SIG_BOX_W_MM = 128;
      const SIG_BOX_H_MM = 25;
      const SIG_LABEL_HEIGHT_MM = 5;

      const headerCanvas = await html2canvasForPdf(
        html2canvas,
        headerRef.current,
      );
      const headerImg = canvasToJpegDataUrl(headerCanvas);
      const headerHeight = canvasHeightInMm(headerCanvas, pageWidth);

      const footerCanvas = await html2canvasForPdf(
        html2canvas,
        footerRef.current,
      );
      const footerImg = canvasToJpegDataUrl(footerCanvas);
      const footerHeight = canvasHeightInMm(footerCanvas, pageWidth);

      const contentAreaHeightPreview = pageHeight - headerHeight - footerHeight;
      let tabelaMarginAdded = false;
      if (tabelaRef.current && contentRef.current) {
        const contentRect = contentRef.current.getBoundingClientRect();
        const tableRect = tabelaRef.current.getBoundingClientRect();
        const pxToMm = pageWidth / contentRect.width;
        const tableTopMm = (tableRect.top - contentRect.top) * pxToMm;
        const tableHeightMm = tableRect.height * pxToMm;
        const tableBottomMm = tableTopMm + tableHeightMm;
        const tableStartPage = Math.floor(tableTopMm / contentAreaHeightPreview);
        const pageBreakMm = (tableStartPage + 1) * contentAreaHeightPreview;
        if (tableBottomMm > pageBreakMm) {
          const extraMarginMm = pageBreakMm - tableTopMm;
          tabelaRef.current.style.marginTop = `${extraMarginMm / pxToMm}px`;
          tabelaMarginAdded = true;
        }
      }

      const qrPrevDisplay = qrRef.current ? qrRef.current.style.display : "";
      if (qrRef.current) qrRef.current.style.display = "none";

      const contentCanvas = await html2canvasForPdf(
        html2canvas,
        contentRef.current,
      );

      if (qrRef.current) qrRef.current.style.display = qrPrevDisplay;

      let qrImg = null;
      let qrWidthMm = 0;
      let qrHeightMm = 0;
      if (qrRef.current && contentRef.current) {
        const qrCanvas = await html2canvasForPdf(html2canvas, qrRef.current, {
          backgroundColor: "#ffffff",
        });
        qrImg = canvasToJpegDataUrl(qrCanvas);
        const contentRect = contentRef.current.getBoundingClientRect();
        const mmPerDomPx = pageWidth / contentRect.width;
        const qrDomRect = qrRef.current.getBoundingClientRect();
        qrWidthMm = qrDomRect.width * mmPerDomPx;
        qrHeightMm = qrDomRect.height * mmPerDomPx;
      }

      if (tabelaMarginAdded) {
        tabelaRef.current.style.marginTop = "";
      }

      const paging = getContentPaging(
        contentCanvas,
        headerHeight,
        footerHeight,
        pageWidth,
        pageHeight,
        { signatureBoxYTopMm: SIG_BOX_Y_TOP_MM },
      );
      const totalContentPages = paging.totalPages;

      const lastContentPageIndex = totalContentPages - 1;
      const contentBottomYOnLastPage =
        headerHeight + paging.lastSliceHeightMm;
      const maxYOnPage = pageHeight - footerHeight;
      const qrTopMargin = 2;
      let qrPageIndex = lastContentPageIndex;
      let qrTargetY = contentBottomYOnLastPage + qrTopMargin;
      if (qrImg && qrTargetY + qrHeightMm > maxYOnPage) {
        qrPageIndex = totalContentPages;
        qrTargetY = headerHeight + qrTopMargin;
      }
      const totalPages = qrImg
        ? Math.max(totalContentPages, qrPageIndex + 1)
        : totalContentPages;

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        if (page < totalContentPages) {
          addSlicedContentImage(
            pdf,
            contentCanvas,
            page,
            paging,
            pageWidth,
            headerHeight,
          );
        }
        pdf.addImage(headerImg, "JPEG", 0, 0, pageWidth, headerHeight);
        pdf.addImage(
          footerImg,
          "JPEG",
          0,
          pageHeight - footerHeight,
          pageWidth,
          footerHeight,
        );
        if (qrImg && page === qrPageIndex) {
          const qrXMm = pageWidth - qrWidthMm - 10;
          pdf.addImage(qrImg, "JPEG", qrXMm, qrTargetY, qrWidthMm, qrHeightMm);
        }
      }

      const dataAtualFormatada = formatDateToShow(new Date().toISOString());
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.2);
      pdf.setTextColor(0, 0, 0);
      pdf.setFillColor(255, 255, 255);
      pdf.rect(SIG_DATE_X_MM, SIG_BOX_Y_TOP_MM, SIG_DATE_W_MM, SIG_BOX_H_MM, "F");
      pdf.rect(SIG_BOX_X_MM, SIG_BOX_Y_TOP_MM, SIG_BOX_W_MM, SIG_BOX_H_MM, "F");

      pdf.rect(SIG_DATE_X_MM, SIG_BOX_Y_TOP_MM, SIG_DATE_W_MM, SIG_BOX_H_MM);
      pdf.setFontSize(8);
      pdf.text("Data", SIG_DATE_X_MM + SIG_DATE_W_MM / 2, SIG_BOX_Y_TOP_MM + 5, {
        align: "center",
      });
      pdf.setFontSize(10);
      pdf.text(
        dataAtualFormatada,
        SIG_DATE_X_MM + SIG_DATE_W_MM / 2,
        SIG_BOX_Y_TOP_MM + SIG_BOX_H_MM / 2 + 3,
        { align: "center" }
      );

      pdf.rect(SIG_BOX_X_MM, SIG_BOX_Y_TOP_MM, SIG_BOX_W_MM, SIG_BOX_H_MM);
      pdf.setFontSize(8);
      pdf.text(
        "Assinatura Digital:",
        SIG_BOX_X_MM + 2,
        SIG_BOX_Y_TOP_MM + SIG_LABEL_HEIGHT_MM - 1
      );
      if (assinaturaDataUrl) {
        const sigImgX = SIG_BOX_X_MM + 2;
        const sigImgY = SIG_BOX_Y_TOP_MM + SIG_LABEL_HEIGHT_MM;
        const sigImgW = SIG_BOX_W_MM - 4;
        const sigImgH = SIG_BOX_H_MM - SIG_LABEL_HEIGHT_MM - 1;
        pdf.addImage(
          assinaturaDataUrl,
          resolverFormatoImagemAssinatura(assinaturaDataUrl),
          sigImgX,
          sigImgY,
          sigImgW,
          sigImgH
        );
      } else if (assinaturaMensagem) {
        pdf.setFontSize(7);
        const lines = pdf.splitTextToSize(assinaturaMensagem, SIG_BOX_W_MM - 4);
        pdf.text(lines, SIG_BOX_X_MM + 2, SIG_BOX_Y_TOP_MM + SIG_LABEL_HEIGHT_MM + 4);
      }

      const dataAtual = new Date().toISOString().split("T")[0];
      const nomeArquivo = `Autorizacao-${id}-${dataAtual}.pdf`;
      const pdfBlob = pdf.output("blob");
      return new File([pdfBlob], nomeArquivo, { type: "application/pdf" });
    });
  };

  const handleEmitir = async () => {
    try {
      setEmitindo(true);
      setError("");

      if (!solicitacao.numeroProcesso && !ehNumeroInformadoValido(numeroInformado)) {
        setError("Informe um número de processo válido (apenas dígitos).");
        return;
      }

      const pdfFile = await gerarPdfBlob();
      const extras = {
        condicionantes,
        observacoes,
      };
      if (!solicitacao.numeroProcesso) {
        extras.numeroInformado = numeroInformado.trim();
      }
      await autorizacoesService.emitirAutorizacao(id, pdfFile, extras);
      setSucesso(true);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao emitir a autorização."));
    } finally {
      setEmitindo(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!solicitacao) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Solicitação não encontrada.</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Voltar
          </Button>
        </Box>
      </Container>
    );
  }

  const nomeRazaoSocial = obterNomeExibicaoUsuario(
    solicitante,
    "Não informado",
  );
  const rotuloNomeSolicitante = obterRotuloNomeSolicitante(solicitante);

  const cpfFormatado = solicitante?.cpf ? formatCpfCnpj(solicitante.cpf) : "Não informado";

  const enderecoCompleto = (() => {
    if (!solicitante?.endereco) return "Não informado";
    const e = solicitante.endereco;
    const partes = [
      e.rua,
      e.numero,
      e.bairro,
      e.municipio,
      e.uf,
      e.cep ? `CEP ${formatCEP(e.cep)}` : null,
    ].filter(Boolean);
    return partes.length > 0 ? partes.join(", ") : "Não informado";
  })();

  let prefixoProcesso = "";
  try {
    prefixoProcesso = obterCodigoTipoAutorizacao(solicitacao.tipoAutorizacao);
  } catch {
    prefixoProcesso = "";
  }
  const numeroProcessoExibicao =
    solicitacao.numeroProcesso ||
    montarNumeroProcessoPreview(prefixoProcesso, numeroInformado, anoProcesso);
  const podeEmitir =
    Boolean(solicitacao.numeroProcesso) || ehNumeroInformadoValido(numeroInformado);

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mt: 4, mb: 8 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>
          Voltar
        </Button>

        {sucesso ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "success.light",
              border: 2,
              borderColor: "success.main",
              borderRadius: 2,
              mb: 3,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Autorização Emitida com Sucesso!
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              A autorização foi emitida e está pronta para download.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ justifyContent: "center", alignItems: "stretch" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/autorizacao/${id}`)}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Ver Detalhes
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Voltar
              </Button>
            </Stack>
          </Paper>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", md: "center" },
                gap: 2,
                mb: 4,
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, fontSize: { xs: "1.1rem", md: "1.5rem" } }}
              >
                Emitir Autorização
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: "stretch" }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={emitindo}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleEmitir}
                  disabled={emitindo || !podeEmitir}
                  fullWidth
                  sx={{ minWidth: { sm: 200 }, width: { xs: "100%", sm: "auto" } }}
                >
                  {emitindo ? <CircularProgress size={22} color="inherit" /> : "Emitir Autorização"}
                </Button>
              </Stack>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              Ao emitir a autorização, você confirma que todos os requisitos foram atendidos.
              O documento será gerado em PDF e a solicitação passará para o status "Emitida".
            </Alert>

            <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
              {/* Coluna Esquerda - Formulário */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        Tipo de Autorização
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tipoAutorizacaoLabel(solicitacao.tipoAutorizacao)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {solicitacao.numeroProcesso ? (
                        <>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            Número do Processo
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {solicitacao.numeroProcesso}
                          </Typography>
                        </>
                      ) : (
                        <NumeroProcessoField
                          prefixo={prefixoProcesso}
                          value={numeroInformado}
                          onChange={setNumeroInformado}
                          ano={anoProcesso}
                          error={Boolean(numeroInformado) && !ehNumeroInformadoValido(numeroInformado)}
                          helperText={
                            Boolean(numeroInformado) && !ehNumeroInformadoValido(numeroInformado)
                              ? "Informe apenas dígitos (1 a 10)."
                              : undefined
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        Data de Emissão
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDateToShow(new Date().toISOString())}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        Analista
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {usuarioAtual?.data?.username || usuarioAtual?.username || "Não informado"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Identificação do Interessado
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          {rotuloNomeSolicitante}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {nomeRazaoSocial}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          CPF/CNPJ
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {cpfFormatado}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Endereço
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {enderecoCompleto}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Dados do Evento
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Nome do Evento
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {solicitacao.nomeEvento || "Não informado"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Dia(s) do Evento
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {solicitacao.dataEvento
                            ? formatDateToShow(solicitacao.dataEvento)
                            : "Não informado"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          Hora da Vigência da Autorização
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Hora início"
                              type="time"
                              fullWidth
                              size="small"
                              value={horaInicio}
                              onChange={(e) => setHoraInicio(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Hora fim"
                              type="time"
                              fullWidth
                              size="small"
                              value={horaFim}
                              onChange={(e) => setHoraFim(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Local do Evento
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {solicitacao.localEvento || "Não informado"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Observações
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      value={observacoes}
                      onChange={(e) => setObservacoes(e.target.value)}
                      placeholder="Insira as observações desta autorização..."
                      variant="outlined"
                      inputProps={{ maxLength: OBSERVACOES_MAX }}
                      InputProps={{
                        endAdornment: (
                          <CharCounter
                            value={observacoes}
                            maxLength={OBSERVACOES_MAX}
                          />
                        ),
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Condicionantes
                    </Typography>
                    <TextField
                      multiline
                      rows={8}
                      fullWidth
                      value={condicionantes}
                      onChange={(e) => setCondicionantes(e.target.value)}
                      placeholder="Descreva as condicionantes desta autorização..."
                      variant="outlined"
                      inputProps={{ maxLength: CONDICIONANTES_MAX }}
                      InputProps={{
                        endAdornment: (
                          <CharCounter
                            value={condicionantes}
                            maxLength={CONDICIONANTES_MAX}
                          />
                        ),
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Coluna Direita - Prévia do PDF */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    backgroundColor: "background.default",
                    border: 1,
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textAlign: "center",
                      mb: 3,
                      fontWeight: 600,
                      color: "text.secondary",
                      fontSize: "12px",
                    }}
                  >
                    PRÉVIA DO DOCUMENTO
                  </Typography>

                  <Box
                    sx={{
                      overflowX: "auto",
                      overflowY: "auto",
                      maxHeight: { xs: 400, md: 600 },
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      flex: 1,
                    }}
                  >
                  <Box
                    ref={previewRef}
                    sx={{
                      width: "100%",
                      maxWidth: `${PDF_DOCUMENT_WIDTH_PX}px`,
                      minWidth: { xs: 320, sm: "auto" },
                      display: "flex",
                      flexDirection: "column",
                      mx: "auto",
                    }}
                  >
                    {/* CABEÇALHO */}
                    <Box
                      ref={headerRef}
                      sx={{
                        backgroundColor: "white",
                        overflow: "hidden",
                        borderRadius: "4px 4px 0 0",
                        height: 80,
                      }}
                    >
                      {headerImgSrc && (
                        <img
                          src={headerImgSrc}
                          alt="Cabeçalho da autorização"
                          style={{
                            width: "100%",
                            height: "80px",
                            objectFit: "fill",
                            display: "block",
                          }}
                        />
                      )}
                    </Box>

                    {/* CONTEÚDO */}
                    <Box
                      ref={contentRef}
                      sx={{
                        backgroundColor: "#ffffff",
                        p: 3,
                        fontSize: "11px",
                        lineHeight: 1.6,
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={8}>
                          <Box
                            sx={{
                              border: "1px solid #000",
                              p: 1,
                              textAlign: "center",
                              height: 80,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "15px",
                                fontWeight: "bold",
                                color: "black",
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                              }}
                            >
                              Autorização - {tipoAutorizacaoLabel(solicitacao.tipoAutorizacao)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box
                            sx={{
                              border: "1px solid #000",
                              p: 1,
                              textAlign: "center",
                              height: 80,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
                              Processo:
                            </Typography>
                            <Typography
                              sx={{ fontSize: "10px", fontWeight: "bold", wordBreak: "break-all" }}
                            >
                              {numeroProcessoExibicao || "—"}
                            </Typography>
                            <Typography sx={{ fontSize: "9px", mt: 0.5 }}>
                              Emissão: {formatDateToShow(new Date().toISOString())}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Identificação */}
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5 }}>
                          Identificação do Interessado
                        </Typography>
                        <Box sx={{ border: "1px solid #000", p: 1.5 }}>
                          <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                            <strong style={{ fontSize: "11px" }}>{rotuloNomeSolicitante}:</strong> {nomeRazaoSocial}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                            <strong style={{ fontSize: "11px" }}>CPF/CNPJ:</strong> {cpfFormatado}
                          </Typography>
                          <Typography sx={{ fontSize: "11px" }}>
                            <strong style={{ fontSize: "11px" }}>Endereço:</strong> {enderecoCompleto}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Evento */}
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5 }}>
                          Dados do Evento
                        </Typography>
                        <Box sx={{ border: "1px solid #000", p: 1.5 }}>
                          <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                            <strong style={{ fontSize: "11px" }}>Nome do Evento:</strong>{" "}
                            {solicitacao.nomeEvento || "____________________"}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                            <strong style={{ fontSize: "11px" }}>Dia(s) do Evento:</strong>{" "}
                            {solicitacao.dataEvento
                              ? formatDateToShow(solicitacao.dataEvento)
                              : "____________________"}
                            {horaInicio && horaFim
                              ? ` (das ${horaInicio} às ${horaFim} hrs)`
                              : horaInicio
                              ? ` (a partir das ${horaInicio} hrs)`
                              : ""}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                            <strong style={{ fontSize: "11px" }}>Local do Evento:</strong>{" "}
                            {solicitacao.localEvento || "____________________"}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", whiteSpace: "pre-wrap" }}>
                            <strong style={{ fontSize: "11px" }}>Observações:</strong>{" "}
                            {observacoes || "Nenhuma observação."}
                          </Typography>
                        </Box>
                      </Box>

                      {/* IMPORTANTE */}
                      <Box
                        sx={{
                          border: "2px solid #000",
                          p: 1.5,
                          mb: 2,
                          backgroundColor: "#fff8e1",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "11px",
                            mb: 0.5,
                            textAlign: "center",
                          }}
                        >
                          IMPORTANTE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            textAlign: "justify",
                            fontWeight: "bold",
                          }}
                        >
                          {IMPORTANTE_TEXTO}
                        </Typography>
                      </Box>

                      {/* CONDICIONANTES */}
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5 }}>
                          CONDICIONANTES
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid #000",
                            p: 1.5,
                            minHeight: 60,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <Typography sx={{ fontSize: "11px", whiteSpace: "pre-wrap" }}>
                            {condicionantes ||
                              "Nenhuma condicionante informada."}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Tabela de Limites */}
                      <Box ref={tabelaRef} sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "11px",
                            mb: 0.5,
                            textAlign: "center",
                          }}
                        >
                          Limites de níveis de pressão sonora, segundo a NBR 10.151 / ABNT
                        </Typography>
                        <Box sx={{ overflowX: "auto", width: "100%" }}>
                        <TableContainer
                          component={Box}
                          sx={{ border: "1px solid #000", minWidth: 400 }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    border: "1px solid #000",
                                    backgroundColor: "#f0f0f0",
                                  }}
                                >
                                  Tipos de Áreas
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    border: "1px solid #000",
                                    backgroundColor: "#f0f0f0",
                                  }}
                                >
                                  Diurno (dB)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    border: "1px solid #000",
                                    backgroundColor: "#f0f0f0",
                                  }}
                                >
                                  Noturno (dB)
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {NIVEIS_PRESSAO_SONORA.map((row) => (
                                <TableRow key={row.categoria}>
                                  <TableCell
                                    sx={{
                                      fontSize: "10px",
                                      border: "1px solid #000",
                                    }}
                                  >
                                    {row.categoria}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "10px",
                                      border: "1px solid #000",
                                    }}
                                  >
                                    {row.diurno}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "10px",
                                      border: "1px solid #000",
                                    }}
                                  >
                                    {row.noturno}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        </Box>
                      </Box>

                      {/* QR Code de validação */}
                      <Box
                        ref={qrRef}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <QRCodeSVG
                            value={`${window.location.origin}/validar/autorizacao/${id}`}
                            size={70}
                            level="M"
                          />
                          <Typography
                            sx={{ fontSize: "8px", mt: 0.5, textAlign: "center" }}
                          >
                            Autenticidade
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* FOOTER */}
                    <Box
                      ref={footerRef}
                      sx={{
                        backgroundColor: "white",
                        overflow: "hidden",
                        borderRadius: "0 0 4px 4px",
                        height: 60,
                      }}
                    >
                      {footerImgSrc && (
                        <img
                          src={footerImgSrc}
                          alt="Rodapé da autorização"
                          style={{
                            width: "100%",
                            height: "60px",
                            objectFit: "fill",
                            display: "block",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Container>
  );
}

export default EmitirAutorizacao;
